import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { createPayableBill, fetchPayables, fetchVendors, payPayableBill, postPayableBill } from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { todayIsoDate, useLatestRef } from "./accountingUtils";

const AP_STATUS = {
  draft: { label: "Draft", color: "secondary" },
  open: { label: "Open", color: "info" },
  partial: { label: "Partial", color: "warning" },
  paid: { label: "Paid", color: "success" },
  void: { label: "Void", color: "dark" },
};

const AccountsPayablePage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendor_id: "",
    bill_number: "",
    bill_date: todayIsoDate(),
    due_date: todayIsoDate(),
    amount: "",
    description: "",
  });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(
      () => Promise.all([fetchPayables(branchId).then(setRows), fetchVendors(branchId).then(setVendors)]),
      { errorMessage: "Failed to load payables." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load payables.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const loadRef = useLatestRef(load);

  const action = useCallback(
    (row, type) => {
      const meta = type === "post"
        ? { title: "Post bill?", label: "Post", msg: "Bill becomes open and payable." }
        : { title: "Record payment?", label: "Pay", msg: "Apply full remaining balance." };
      confirm({
        title: meta.title,
        message: meta.msg,
        confirmLabel: meta.label,
        successMessage: type === "post" ? "Bill posted." : "Payment recorded.",
        onConfirm: async () => {
          if (type === "post") await postPayableBill(row.id);
          else await payPayableBill(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Bill #", selector: (r) => r.bill_number, width: "110px" },
      { name: "Vendor", selector: (r) => r.vendor_name, grow: 2 },
      { name: "Due", cell: (r) => formatDate(r.due_date), width: "110px" },
      { name: "Amount", cell: (r) => formatTZS(r.amount), right: true, width: "130px" },
      { name: "Balance", cell: (r) => formatTZS(r.balance_due), right: true, width: "130px" },
      {
        name: "Status",
        cell: (r) => {
          const m = AP_STATUS[r.status] || { label: r.status, color: "secondary" };
          return <Badge color={m.color}>{m.label}</Badge>;
        },
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              r.status === "draft" && {
                icon: "check",
                label: "Post bill",
                onClick: () => action(r, "post"),
                variant: "primary",
              },
              (r.status === "open" || r.status === "partial") && {
                icon: "payment",
                label: "Pay bill",
                onClick: () => action(r, "pay"),
                variant: "success",
              },
            ].filter(Boolean)}
          />
        ),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [action]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () => createPayableBill({
          branch_id: branchId,
          vendor_id: form.vendor_id,
          bill_number: form.bill_number,
          bill_date: form.bill_date,
          due_date: form.due_date,
          amount: Number(form.amount),
          description: form.description,
        }),
        { successMessage: "Vendor bill created." }
      );
      setModalOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Accounts Payable" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Vendor bills</h5>
            <div className="d-flex gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>New bill</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New vendor bill</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="6"><FormGroup><Label>Vendor</Label>
                  <Input type="select" required value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
                    <option value="">Select vendor</option>
                    {vendors.filter((v) => v.is_active).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </Input></FormGroup></Col>
                <Col md="6"><FormGroup><Label>Bill number</Label>
                  <Input required value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Bill date</Label>
                  <Input type="date" required value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Due date</Label>
                  <Input type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Amount</Label>
                  <Input type="number" min="0.01" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></FormGroup></Col>
                <Col md="12"><FormGroup><Label>Description</Label>
                  <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormGroup></Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save draft"}</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default AccountsPayablePage;
